import { css } from '@emotion/react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { ChatAttributeType, ChatState, ErrorTypes } from 'sdk';

import { useSDK } from '../../../app';
import { useUIState } from '../../../app/App';
import { styled } from '../../../styled';
import { Theme } from '../../../theme/interface/detailed';
import { Button } from '../../buttons';
import { Field } from '../fields/field';
import { getFormValues } from '../utils/getFormValues';
import { msgs } from './msgs';

let lastInput = '';

const SendButton = styled(
	observer(({ onClick, children, className }: { onClick: VoidFunction; children: string; className: string }) => {
		const { sendHistoryToEmailRequestStatus } = useSDK();
		return (
			<Button className={className} disabled={sendHistoryToEmailRequestStatus.isPending} onClick={onClick}>
				{children}
			</Button>
		);
	})
)(({ theme }: { theme: Theme }) => theme.chat.buttons.sendDialogToEmail.sendDialogToEmailButton);

const CancelButton = styled(
	({ className, onClick, children }: { children: string; className: string; onClick: VoidFunction }) => (
		<Button className={className} onClick={onClick}>
			{children}
		</Button>
	)
)(({ theme }: { theme: Theme }) => theme.chat.buttons.sendDialogToEmail.cancelSendingDialogButton);

export const SendDialogToEmailForm = styled(
	observer(({ className }: { className: string }): ReactElement | null => {
		const sdk = useSDK();
		const { uiStateStore, formsStore } = sdk;
		const form = useRef<HTMLFormElement>(null);
		const [validationError, setValidationError] = useState('');
		const { formatMessage } = useIntl();
		const ui = useUIState();
		const send = useCallback(
			async (email: string) => {
				await sdk.sendDialogToEmail(email);
				if (sdk.sendHistoryToEmailRequestStatus.isLoaded) {
					uiStateStore.setChatState(ChatState.afterSendDialogToEmail);
				} else {
					ui.modalStore.push({
						type: ErrorTypes.Info,
						message: formatMessage(msgs.sendError),
					});
				}
			},
			[formatMessage, sdk, ui.modalStore, uiStateStore]
		);

		const onSubmit = useCallback(async (): Promise<void> => {
			if (form.current) {
				setValidationError('');
				const values = getFormValues(form.current);
				const email = values[0].value as string;
				if ((formsStore.rules.EMAIL.value as RegExp).test(email)) {
					send(email);
				} else {
					setValidationError(formatMessage(msgs.incorrectEmail));
				}
			}
		}, [formatMessage, formsStore.rules.EMAIL.value, send]);

		const onCancel = (): void => {
			uiStateStore.setChatState(ChatState.chat);
		};

		const attribute = Object.values(formsStore.allAttributesMap).find((v) => v.type === ChatAttributeType.Email);
		const initialValue = formsStore.cachedValues[attribute?.key || ''] || lastInput;
		useEffect(() => {
			const formRef = form.current;
			return (): void => {
				lastInput = (formRef && getFormValues(formRef)[0])?.value || '';
			};
		});

		return (
			<div className={className}>
				<form onSubmit={(e): Promise<void> => (e.preventDefault(), onSubmit())} ref={form}>
					<Field
						type={ChatAttributeType.Email}
						name="email"
						required
						error={validationError}
						initialValue={initialValue}
						fieldName={formatMessage(msgs.enterYourMail)}
					/>
					<SendButton onClick={onSubmit}>{formatMessage(msgs.sendDialogToEmail)}</SendButton>
					<CancelButton onClick={onCancel}>{formatMessage(msgs.cancel)}</CancelButton>
				</form>
			</div>
		);
	})
)(css`
	display: flex;
	flex-flow: column;
	height: 100%;
	overflow: visible;
	& > form {
		padding: 25px 16px;
	}
`);
